<template>
  <section class="hero-sticky" :class="{ released: finished }">
    <div class="solid-bg"></div>

    <div class="reveal-layer" :class="{ released: finished }" :style="maskStyle"></div>

    <div class="content-layer" :style="contentStyle" aria-live="polite">
      <slot />
    </div>

    <img
      src="@/assets/logo-mask.svg"
      class="logo"
      :style="logoStyle"
      alt="Logo"
      draggable="false"
    />

    <div class="cursor-wrap" :style="cursorWrapStyle">
      <img
        src="@/assets/cursor.png"
        class="cursor-icon"
        :style="cursorStyle"
        alt="Indicador de rolagem"
        draggable="false"
      />
    </div>
  </section>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import logoMask from '@/assets/logo-mask.svg'

const props = defineProps({
  progress: {
    type: Number,
    default: 0
  }
})

const finished = computed(() => props.progress >= 1)
const acceleratedProgress = computed(() => Math.pow(props.progress, 3))

const baseSize = 300
const windowWidth = ref(0)

const updateWindowWidth = () => {
  windowWidth.value = window.innerWidth || 0
}

onMounted(() => {
  updateWindowWidth()
  window.addEventListener('resize', updateWindowWidth)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWindowWidth)
})

const maxScale = computed(() => {
  if (windowWidth.value <= 0) return 1
  return windowWidth.value / (0.09 * baseSize)
})

const scale = computed(() => 1 + acceleratedProgress.value * (maxScale.value - 1))

const maskStyle = computed(() => ({
  WebkitMaskImage: props.progress >= 1 ? 'none' : `url(${logoMask})`,
  WebkitMaskSize: `${baseSize * scale.value}px`,
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
  maskImage: props.progress >= 1 ? 'none' : `url(${logoMask})`,
  maskSize: `${baseSize * scale.value}px`,
  maskRepeat: 'no-repeat',
  maskPosition: 'center'
}))

const logoStyle = computed(() => ({
  width: `${baseSize * scale.value}px`,
  opacity: 1 - props.progress,
  pointerEvents: 'none'
}))

const cursorOpacity = computed(() => Math.max(0, 1 - (props.progress / 0.3)))

const cursorWrapStyle = computed(() => ({
  top: `calc(50% + ${(baseSize * scale.value) / 2 + 28}px)`,
  opacity: cursorOpacity.value
}))

const cursorStyle = computed(() => ({
  width: `${baseSize * scale.value * 0.12}px`
}))

const contentStyle = computed(() => {
  const start = 0.88
  const t = Math.max(0, Math.min(1, (props.progress - start) / (1 - start)))
  return {
    opacity: t,
    pointerEvents: props.progress >= 0.9 ? 'auto' : 'none'
  }
})
</script>

<style scoped>
.hero-sticky {
  position: sticky;
  top: 0;
  height: 100vh;
  width: 100%;
  overflow: hidden;
}

.solid-bg {
  position: absolute;
  inset: 0;
  background: #0b1c2d;
  z-index: 1;
}

.reveal-layer {
  position: absolute;
  inset: 0;
  background: var(--color-yellow);
  opacity: 1;
  z-index: 1;
  pointer-events: none;
}

.reveal-layer.released {
  -webkit-mask-image: none;
  mask-image: none;
}

.content-layer {
  position: absolute;
  inset: 0;
  z-index: 2;
  transition: opacity 0.25s ease;
}

.logo {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-width: none;
  z-index: 3;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

.cursor-wrap {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  pointer-events: none;
}

.cursor-icon {
  display: block;
  height: auto;
  animation: cursor-bob 1.3s ease-in-out infinite;
  user-select: none;
  -webkit-user-drag: none;
}

@keyframes cursor-bob {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(10px);
  }
}
</style>
